<template>
  <div class="substep-review-your-team">
    <div class="substep-header">
      <h3 data-testId="step-title">Review your team</h3>
      <p>Take a look around and explore — this is a preview of what your team's activity will look like in Hubstaff.</p>
    </div>

    <div class="member-pills">
      <button
        v-for="member in teamMembers"
        :key="member.id"
        class="member-pill"
        :class="{ active: selectedMemberId === member.id }"
        @click="selectMember(member.id)"
      >
        <AppAvatar
          :avatar="member.avatar"
          size="speck"
          class="member-pill-avatar"
        />
        {{ member.name }}
      </button>
      <div class="coachmark-anchor coachmark-anchor--right">
        <span class="coachmark coachmark--point-left coachmark--bob-x">View other members</span>
      </div>
    </div>

    <div class="team-member-card">
      <div class="selected-member-header">
        <AppAvatar
          :avatar="selectedMember.avatar"
          size="speck"
          class="member-avatar"
        />
        <div class="member-info">
          <div class="member-name">{{ selectedMember.name }}</div>
          <div class="member-working-on">
            <span class="status-icon"></span>
            Working on <strong>{{ selectedMember.working_on }}</strong>
          </div>
        </div>
      </div>

      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-label">Total work time</div>
          <div class="stat-value">{{ selectedStats.total_work_time }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Avg. activity</div>
          <div class="stat-value stat-value--activity">{{ selectedStats.avg_activity }}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Idle time</div>
          <div class="stat-value">{{ selectedStats.idle_time }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Manual time</div>
          <div class="stat-value">{{ selectedStats.manual_time }}</div>
        </div>
      </div>

      <div class="time-activity-section">
        <div class="section-header">
          <span class="section-title">Time &amp; activity</span>
        </div>

        <div class="day-cards">
          <div
            v-for="day in selectedDays"
            :key="day.date"
            class="day-card"
            :class="{ 'has-activity': day.tracked_seconds > 0, 'is-active': activeDayDate === day.date }"
            @click="toggleDay(day)"
          >
            <div
              v-if="day.date === firstUnusualDate"
              class="coachmark-anchor coachmark-anchor--top"
            >
              <span class="coachmark coachmark--point-down coachmark--bob-y"> Hover to see what happened here </span>
            </div>
            <div class="day-alert-slot">
              <span
                v-if="day.unusual_activities_count"
                v-tooltip="{ placement: 'top' }"
                :data-original-title="unusualTooltip(day)"
                class="day-warning"
              ></span>
            </div>
            <div class="day-date">{{ day.day_label }}</div>
            <div class="day-progress">
              <div
                class="day-progress-fill"
                :style="{ width: progressWidth(day) + '%' }"
              ></div>
            </div>
            <div
              class="day-time"
              :class="{ 'is-empty': !day.tracked_seconds }"
            >
              {{ formatTime(day.tracked_seconds) }}
            </div>
            <span
              v-if="day.tracked_seconds > 0"
              class="day-activity-badge"
            >
              {{ day.activity_percent }}%
            </span>
            <span
              v-else
              class="day-activity-badge day-activity-badge--empty"
            >
              -
            </span>
            <div
              v-if="day.screenshots.length"
              class="day-thumb"
              :style="{ backgroundImage: `url(${day.screenshots[0].thumb_url})` }"
            >
              <span class="day-thumb-count">{{ day.screenshots.length }} screenshots</span>
            </div>
            <div
              v-else
              class="day-thumb day-thumb--empty"
            ></div>
            <div
              v-if="day.date === screenshotCoachmarkDate"
              class="coachmark-anchor coachmark-anchor--bottom"
            >
              <span class="coachmark coachmark--point-up coachmark--bob-y"> Click to see screenshots for the day </span>
            </div>
          </div>
        </div>

        <div
          v-if="activeDayScreenshots.length"
          class="screenshots-panel"
        >
          <p class="screenshots-hint">Screenshots for {{ activeDayLabel }}</p>
          <div class="screenshot-thumbs">
            <img
              v-for="(screenshot, index) in activeDayScreenshots"
              :key="index"
              :src="screenshot.thumb_url"
              alt="Screenshot"
              class="screenshot-thumb"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="weekly-report-note">
      <i class="hi hi-mail"></i>
      A weekly report with your team's key activity is automatically sent to your inbox every week.
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import AppAvatar from '~/components/AppAvatar.vue';

export default {
  name: 'ReviewYourTeam',

  components: { AppAvatar },

  mixins: [GettingStartedMixin],

  props: {
    teamMembers: {
      type: Array,
      default: () => [],
    },
    teamMemberStats: {
      type: Object,
      default: () => ({}),
    },
    teamMemberDays: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      selectedMemberId: this.teamMembers.length ? this.teamMembers[0].id : null,
      activeDayDate: null,
    };
  },

  computed: {
    selectedMember() {
      return this.getTeamMemberById(this.selectedMemberId) || {};
    },
    selectedStats() {
      return this.teamMemberStats[this.selectedMemberId] || {};
    },
    selectedDays() {
      return this.teamMemberDays[this.selectedMemberId] || [];
    },
    firstUnusualDate() {
      const day = this.selectedDays.find((d) => d.unusual_activities_count);

      return day ? day.date : null;
    },
    screenshotCoachmarkDate() {
      const days = this.selectedDays.filter((d) => d.screenshots.length);

      return days.length ? days[days.length - 1].date : null;
    },
    maxTrackedSeconds() {
      const seconds = this.selectedDays.map((d) => d.tracked_seconds);

      return Math.max.apply(null, seconds.concat([1]));
    },
    activeDay() {
      return this.selectedDays.find((d) => d.date === this.activeDayDate);
    },
    activeDayScreenshots() {
      return this.activeDay ? this.activeDay.screenshots : [];
    },
    activeDayLabel() {
      return this.activeDay ? this.activeDay.day_label : '';
    },
  },

  watch: {
    selectedMemberId() {
      this.activeDayDate = null;
    },
  },

  beforeMount() {
    this.$emit('completeSubstep');
  },

  methods: {
    selectMember(memberId) {
      this.selectedMemberId = memberId;
    },
    unusualTooltip(day) {
      const count = day.unusual_activities_count;

      return `${count} unusual ${count === 1 ? 'activity' : 'activities'}`;
    },
    toggleDay(day) {
      if (!day.tracked_seconds) {
        return;
      }

      this.activeDayDate = this.activeDayDate === day.date ? null : day.date;
    },
    progressWidth(day) {
      if (!day.tracked_seconds) {
        return 0;
      }

      return Math.round((day.tracked_seconds / this.maxTrackedSeconds) * 100);
    },
    formatTime(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    },
  },
};
</script>
